import React from 'react';
import { Check } from 'lucide-react';
import {
  DAYS, cellKey, shortCodeOf, toggleCell, setCellCount, setCellLeadRank, setCellSupportRank,
  setCellPerPosition, setCellSlotRank,
} from '../../services/patternCells';

/**
 * The operating-pattern editor: which shift types a location runs on which
 * days, and how many lead / support bodies each session needs.
 *
 * Controlled -- the parent owns `cells` and receives every change through
 * `onChange`. Used by BOTH the clinic-creation modal and the clinic drawer, so
 * a clinic can be fully configured at creation instead of being created empty
 * and then set up a second time from scratch.
 *
 * Columns are keyed by shift type ID. Keying by start time (which the creation
 * modal used to do) cannot distinguish two types that share one -- IP runs MID
 * and SB both 09:00-18:00 -- so they collapsed into a single toggle.
 */
export default function PatternGrid({ shiftTypes = [], cells = {}, onChange,
                                      rankLabels = {}, compact = false }) {
  if (!shiftTypes.length) {
    return (
      <div className="rounded-2xl border border-dashed border-slate-200 px-4 py-8 text-center">
        <p className="text-[10px] font-bold text-slate-400 uppercase tracking-widest">
          No shift types defined
        </p>
        <p className="text-[10px] text-slate-400 mt-1">Create them in the Shift Types tab first.</p>
      </div>
    );
  }

  const toggle = (day, typeId) => onChange(toggleCell(cells, day, typeId));
  const setCount = (day, typeId, group, field, raw) =>
    onChange(setCellCount(cells, day, typeId, group, field, raw));
  const setRank = (day, typeId, raw) => onChange(setCellLeadRank(cells, day, typeId, raw));
  const setSupportRank = (day, typeId, end, raw) =>
    onChange(setCellSupportRank(cells, day, typeId, end, raw));
  const setPerPosition = (day, typeId, on) => onChange(setCellPerPosition(cells, day, typeId, on));
  const setSlotRank = (day, typeId, position, end, raw) =>
    onChange(setCellSlotRank(cells, day, typeId, position, end, raw));
  const bandSelects = (value, onSet, label) => (
    <div className="flex-1 min-w-0 flex items-center gap-1">
      <select aria-label={`${label} most senior`} value={value?.minRank ?? ''}
        onChange={(e) => onSet('minRank', e.target.value)}
        title="Most senior rank allowed (e.g. not a consultant)"
        className="flex-1 min-w-0 h-7 px-1 text-[10px] font-bold text-slate-600 bg-white border border-slate-200 rounded-md outline-none focus:border-emerald-400 cursor-pointer">
        <option value="">Most senior: any</option>
        {[0, 1, 2, 3, 4].map(r => <option key={r} value={r}>{`From ${rankName(r)}`}</option>)}
      </select>
      <select aria-label={`${label} least senior`} value={value?.maxRank ?? ''}
        onChange={(e) => onSet('maxRank', e.target.value)}
        title="Least senior rank allowed"
        className="flex-1 min-w-0 h-7 px-1 text-[10px] font-bold text-slate-600 bg-white border border-slate-200 rounded-md outline-none focus:border-emerald-400 cursor-pointer">
        <option value="">Least senior: any</option>
        {[0, 1, 2, 3, 4].map(r => <option key={r} value={r}>{`To ${rankName(r)}`}</option>)}
      </select>
    </div>
  );
  const rankName = (r) => {
    const nm = (rankLabels[String(r)] || '').trim();
    return nm ? `${nm} (${r})` : `rank ${r}`;
  };

  return (
    <div className="bg-slate-50 p-3 rounded-2xl border border-slate-100 shadow-inner space-y-2">
      <div className="flex gap-1.5 mb-0.5">
        <div className="w-9 flex-shrink-0" />
        {shiftTypes.map(t => {
          const code = shortCodeOf(t);
          const isStd = code === 'AM' || code === 'PM';
          return (
            <div key={t.id}
                 className={`flex-1 text-center text-[9px] font-bold uppercase tracking-[0.15em] ${isStd ? 'text-slate-300' : 'text-teal-400'}`}>
              {code}
            </div>
          );
        })}
      </div>

      {DAYS.map((day, idx) => (
        <React.Fragment key={day}>
          {idx === 5 && (
            <div className="flex items-center gap-1.5 pt-1.5 pb-0.5">
              <div className="w-9 flex-shrink-0" />
              <div className="flex-1 text-[8px] font-bold text-slate-300 uppercase tracking-[0.2em]">
                Weekend (optional)
              </div>
            </div>
          )}
          <div className={`flex items-start gap-1.5 ${idx >= 5 ? 'opacity-90' : ''}`}>
            <div className="w-9 flex-shrink-0 text-[9px] font-bold text-slate-400 uppercase tracking-wider pt-2.5">
              {day.slice(0, 3)}
            </div>
            {shiftTypes.map(t => {
              const typeId = String(t.id);
              const cell = cells[cellKey(idx, typeId)];
              const on = Boolean(cell);
              const code = shortCodeOf(t);
              return (
                <div key={typeId} className="flex-1 min-w-0">
                  <button
                    type="button"
                    onClick={() => toggle(idx, typeId)}
                    title={on ? `${code} on — edit counts below` : `Enable ${code}`}
                    className={`w-full h-9 rounded-xl border-2 transition-all flex items-center justify-center gap-1 ${on ? 'bg-emerald-50 border-emerald-500 text-emerald-600 shadow-sm' : 'bg-white border-slate-100 text-slate-300 hover:border-slate-300'}`}
                  >
                    <Check className={`w-3.5 h-3.5 font-bold ${on ? 'opacity-100' : 'opacity-0'}`} />
                    {on && (
                      <span className="text-[8px] font-black tabular-nums">
                        {(cell.lead?.target || 1)}L·{(cell.support?.target || 0)}S
                      </span>
                    )}
                  </button>
                </div>
              );
            })}
          </div>

          {/* Full-width editors for this day's ACTIVE cells. Moving the inputs
              out of the narrow per-type column gives each control room to
              breathe, regardless of how many shift types exist. */}
          {shiftTypes.some(t => cells[cellKey(idx, String(t.id))]) && (
            <div className="ml-9 mt-1.5 space-y-1.5">
              {shiftTypes.map(t => {
                const typeId = String(t.id);
                const cell = cells[cellKey(idx, typeId)];
                if (!cell) return null;
                const code = shortCodeOf(t);
                return (
                  <div key={typeId} className="rounded-xl border border-slate-200 bg-white p-2">
                    <div className="flex items-center justify-between mb-1.5">
                      <span className="text-[9px] font-black uppercase tracking-widest text-emerald-700">{code}</span>
                      <button type="button" onClick={() => toggle(idx, typeId)} title="Turn off"
                              className="text-[8px] font-bold uppercase text-slate-300 hover:text-rose-500">
                        remove
                      </button>
                    </div>

                    {/* LEAD */}
                    <div className="flex items-center gap-2 mb-1.5">
                      <span className="w-12 flex-shrink-0 text-[9px] font-black uppercase tracking-wider text-slate-600">Lead</span>
                      <label className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-[8px] font-bold uppercase text-slate-400">min</span>
                        <input type="number" min="1" max="99" value={cell.lead?.min ?? 1}
                          onChange={(e) => setCount(idx, typeId, 'lead', 'min', e.target.value)}
                          className="w-12 h-7 px-1 text-center text-xs font-bold text-slate-700 bg-white border border-slate-300 rounded-md outline-none focus:border-emerald-400" />
                      </label>
                      <label className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-[8px] font-bold uppercase text-emerald-600">target</span>
                        <input type="number" min="1" max="99" value={cell.lead?.target ?? 1}
                          onChange={(e) => setCount(idx, typeId, 'lead', 'target', e.target.value)}
                          className="w-12 h-7 px-1 text-center text-xs font-bold text-emerald-800 bg-emerald-50/60 border border-emerald-300 rounded-md outline-none focus:border-emerald-400" />
                      </label>
                      <select value={cell.lead?.maxRank ?? ''}
                        onChange={(e) => setRank(idx, typeId, e.target.value)}
                        title="Lead seniority ceiling"
                        className="flex-1 min-w-0 h-7 px-1.5 text-[10px] font-bold text-slate-600 bg-white border border-slate-200 rounded-md outline-none focus:border-emerald-400 cursor-pointer">
                        <option value="">Any rank</option>
                        {[0, 1, 2, 3, 4].map(r => {
                          const nm = (rankLabels[String(r)] || '').trim();
                          return <option key={r} value={r}>{nm ? `≤ ${nm} (${r})` : `≤ rank ${r}`}</option>;
                        })}
                      </select>
                    </div>

                    {/* SUPPORT */}
                    <div className="flex items-center gap-2">
                      <span className="w-12 flex-shrink-0 text-[9px] font-black uppercase tracking-wider text-slate-400">Support</span>
                      <label className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-[8px] font-bold uppercase text-slate-400">min</span>
                        <input type="number" min="0" max="99" value={cell.support?.min ?? 0}
                          onChange={(e) => setCount(idx, typeId, 'support', 'min', e.target.value)}
                          className="w-12 h-7 px-1 text-center text-xs font-bold text-slate-600 bg-white border border-slate-200 rounded-md outline-none focus:border-emerald-400" />
                      </label>
                      <label className="flex items-center gap-1 flex-shrink-0">
                        <span className="text-[8px] font-bold uppercase text-emerald-600">target</span>
                        <input type="number" min="0" max="99" value={cell.support?.target ?? 0}
                          onChange={(e) => setCount(idx, typeId, 'support', 'target', e.target.value)}
                          className="w-12 h-7 px-1 text-center text-xs font-bold text-emerald-700 bg-emerald-50/60 border border-emerald-200 rounded-md outline-none focus:border-emerald-400" />
                      </label>
                      {/* Optional rank band for the support slots: the most and
                          least senior rank allowed. "Any" leaves that end open. */}
                      {!cell.support?.slots && bandSelects(cell.support,
                        (end, raw) => setSupportRank(idx, typeId, end, raw), `${code} support`)}
                    </div>

                    {/* A band for EACH support position (Oct 2026): main's
                        "special" OT session is one Senior Resident and one
                        MO/Resident, which a single band cannot say. */}
                    {((cell.support?.target || 0) > 1 || cell.support?.slots) && (
                      <label className="flex items-center gap-1.5 mt-1.5 ml-14 text-[9px] font-bold text-slate-500 cursor-pointer w-fit">
                        <input type="checkbox" checked={Boolean(cell.support?.slots)}
                          onChange={(e) => setPerPosition(idx, typeId, e.target.checked)}
                          aria-label={`${DAYS[idx]} ${code}: a band for each support position`} />
                        A different rank band for each support position
                      </label>
                    )}
                    {cell.support?.slots && Array.from({ length: cell.support?.target || 0 }, (_, p) => (
                      <div key={p} className="flex items-center gap-2 mt-1 ml-14">
                        <span className="w-16 flex-shrink-0 text-[9px] font-bold text-slate-400">Position {p + 1}</span>
                        {bandSelects(cell.support.slots[p] || { minRank: cell.support.minRank, maxRank: cell.support.maxRank },
                          (end, raw) => setSlotRank(idx, typeId, p, end, raw), `${code} support position ${p + 1}`)}
                      </div>
                    ))}
                  </div>
                );
              })}
            </div>
          )}
        </React.Fragment>
      ))}

      {!compact && (
        <div className="flex items-center gap-3 pt-2 ml-9 text-[8px] font-bold uppercase tracking-widest flex-wrap">
          <span className="text-slate-500">Toggle a shift type, then set Lead / Support counts below</span>
          <span className="text-slate-400">min = mandatory</span>
          <span className="text-emerald-600">target = total</span>
          <span className="text-slate-400">Lead rank = seniority ceiling</span>
          <span className="text-slate-400">Support from/to = rank band (optional)</span>
        </div>
      )}
    </div>
  );
}
